import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import React, { useState } from 'react';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    BodySmallEmphasized,
    BodySmallRegular,
    H6Emphasized,
    Spinner,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import { Button } from '../../common/components/GroveButtons';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import {
    useForkDbosWorkflow,
    useGetDbosWorkflowDetail,
} from '../hooks/useDbosData';
import type { DbosAppMetadata, DbosWorkflowStep } from '../types/DbosTypes';
import {
    DBOS_STATUS_COLORS,
    formatDurationMs,
    formatEpochMs,
} from './dbosConstants';

interface DbosWorkflowDetailModalProps {
    workflowId: string | null;
    isOpen: boolean;
    onClose: () => void;
    onNavigateToWorkflow?: (workflowId: string) => void;
}

export const DbosWorkflowDetailModal = ({
    workflowId,
    isOpen,
    onClose,
    onNavigateToWorkflow,
}: DbosWorkflowDetailModalProps) => {
    const toast = useToastController();
    const [selectedStartStep, setSelectedStartStep] = useState<number>(0);

    const { data, isLoading, error } = useGetDbosWorkflowDetail(
        { workflowId: workflowId ?? '' },
        {
            enabled: isOpen && workflowId != null,
            staleTime: 60_000,
            retry: 1,
            refetchOnWindowFocus: false,
        },
    );

    const forkMutation = useForkDbosWorkflow();

    const handleFork = () => {
        if (workflowId == null) return;

        forkMutation.mutate(
            {
                body: { startStep: selectedStartStep },
                urlParams: { workflowId },
            },
            {
                onSuccess: (response) => {
                    toast.show(
                        `Workflow forked: ${response.forkedWorkflowId}`,
                        MutationSuccessToastOptions,
                    );
                    onClose();
                    if (onNavigateToWorkflow != null) {
                        onNavigateToWorkflow(response.forkedWorkflowId);
                    }
                },
                onError: (err) => {
                    toast.show(
                        'Fork failed',
                        MutationErrorToastOptions(
                            err instanceof Error ? err.message : String(err),
                        ),
                    );
                },
            },
        );
    };

    const isForkable =
        data?.workflow.status === 'ERROR' ||
        data?.workflow.status === 'MAX_RECOVERY_ATTEMPTS_EXCEEDED';

    const stepOptions =
        data?.steps.map((step) => ({
            label: `Step ${step.functionID}: ${step.name}`,
            value: step.functionID,
        })) ?? [];

    return (
        <Dialog
            header={
                workflowId != null
                    ? `Workflow: ${workflowId}`
                    : 'Workflow Detail'
            }
            visible={isOpen}
            onHide={onClose}
            style={{ width: '80vw', maxWidth: 1100 }}
            maximizable
        >
            {isLoading && <Spinner />}

            {error != null && (
                <BodySmallEmphasized color="$red1100">
                    Error loading workflow:{' '}
                    {error instanceof Error ? error.message : String(error)}
                </BodySmallEmphasized>
            )}

            {data != null && (
                <YStack gap={20} padding={8}>
                    {/* Workflow Metadata */}
                    <YStack gap={8}>
                        <H6Emphasized>Metadata</H6Emphasized>
                        <XStack flexWrap="wrap" gap={16}>
                            <MetaField
                                label="Status"
                                value={data.workflow.status}
                                color={
                                    DBOS_STATUS_COLORS[data.workflow.status] ??
                                    '#333'
                                }
                                bold
                            />
                            <MetaField
                                label="Workflow Name"
                                value={data.workflow.workflowName}
                            />
                            <MetaField
                                label="Class"
                                value={data.workflow.workflowClassName}
                            />
                            <MetaField
                                label="Config"
                                value={data.workflow.workflowConfigName}
                            />
                            <MetaField
                                label="Queue"
                                value={data.workflow.queueName ?? '-'}
                            />
                            <MetaField
                                label="Created"
                                value={formatEpochMs(data.workflow.createdAt)}
                            />
                            <MetaField
                                label="Updated"
                                value={formatEpochMs(data.workflow.updatedAt)}
                            />
                            <MetaField
                                label="Recovery Attempts"
                                value={String(data.workflow.recoveryAttempts)}
                            />
                            <MetaField
                                label="Executor"
                                value={data.workflow.executorId}
                            />
                            <MetaField
                                label="App Version"
                                value={data.workflow.applicationVersion}
                            />
                            {data.workflow.forkedFrom != null && (
                                <MetaField
                                    label="Forked From"
                                    value={data.workflow.forkedFrom}
                                    color="#1565c0"
                                    onClick={() => {
                                        if (data.workflow.forkedFrom != null) {
                                            onNavigateToWorkflow?.(
                                                data.workflow.forkedFrom,
                                            );
                                        }
                                    }}
                                />
                            )}
                        </XStack>

                        {/* Error display */}
                        {data.workflow.error != null && (
                            <YStack
                                padding={12}
                                borderRadius={8}
                                style={{ backgroundColor: '#ffebee' }}
                                gap={4}
                            >
                                <BodySmallEmphasized color="$red1100">
                                    Error
                                </BodySmallEmphasized>
                                <BodySmallRegular
                                    style={{ fontFamily: 'monospace' }}
                                >
                                    {data.workflow.error.message}
                                </BodySmallRegular>
                            </YStack>
                        )}

                        {/* Input preview */}
                        {data.workflow.input.length > 0 && (
                            <YStack gap={4}>
                                <BodySmallEmphasized>Input</BodySmallEmphasized>
                                <pre
                                    style={{
                                        fontSize: '0.7rem',
                                        backgroundColor: '#f5f5f5',
                                        padding: 8,
                                        borderRadius: 4,
                                        overflow: 'auto',
                                        maxHeight: 120,
                                    }}
                                >
                                    {JSON.stringify(
                                        data.workflow.input,
                                        null,
                                        2,
                                    )}
                                </pre>
                            </YStack>
                        )}
                    </YStack>

                    {/* Step Execution Trace */}
                    <YStack gap={8}>
                        <H6Emphasized>
                            Step Execution Trace ({data.steps.length} steps)
                        </H6Emphasized>
                        <DataTable
                            value={data.steps}
                            size="small"
                            tableStyle={{
                                width: '100%',
                                fontSize: '0.8rem',
                            }}
                            emptyMessage="No steps recorded"
                        >
                            <Column
                                field="functionID"
                                header="Step"
                                style={{ width: '50px' }}
                                bodyStyle={{ textAlign: 'center' }}
                            />
                            <Column
                                field="name"
                                header="Name"
                                style={{
                                    fontFamily: 'monospace',
                                    fontSize: '0.75rem',
                                }}
                            />
                            <Column
                                header="Duration"
                                body={(row: DbosWorkflowStep) =>
                                    formatDurationMs(
                                        row.completedAtEpochMs -
                                            row.startedAtEpochMs,
                                    )
                                }
                                bodyStyle={{
                                    fontFamily: 'monospace',
                                    fontSize: '0.75rem',
                                }}
                            />
                            <Column
                                header="Output"
                                body={(row: DbosWorkflowStep) => {
                                    if (row.output == null) return '-';
                                    const str = JSON.stringify(row.output);
                                    return (
                                        <span
                                            title={str}
                                            style={{
                                                fontSize: '0.7rem',
                                                fontFamily: 'monospace',
                                            }}
                                        >
                                            {str.length > 60
                                                ? `${str.substring(0, 60)}...`
                                                : str}
                                        </span>
                                    );
                                }}
                            />
                            <Column
                                header="Error"
                                body={(row: DbosWorkflowStep) => {
                                    if (row.error == null) return '-';
                                    return (
                                        <span
                                            style={{
                                                color: '#dc3545',
                                                fontWeight: 'bold',
                                                fontSize: '0.75rem',
                                            }}
                                        >
                                            {row.error.message}
                                        </span>
                                    );
                                }}
                            />
                            <Column
                                header="Child Workflow"
                                body={(row: DbosWorkflowStep) => {
                                    const childId = row.childWorkflowID;
                                    if (childId == null) return '-';
                                    return (
                                        <button
                                            type="button"
                                            style={{
                                                color: '#1565c0',
                                                cursor: 'pointer',
                                                textDecoration: 'underline',
                                                fontSize: '0.7rem',
                                                fontFamily: 'monospace',
                                                background: 'none',
                                                border: 'none',
                                                padding: 0,
                                            }}
                                            onClick={() => {
                                                onNavigateToWorkflow?.(childId);
                                            }}
                                        >
                                            {childId.substring(0, 20)}
                                            ...
                                        </button>
                                    );
                                }}
                            />
                        </DataTable>
                    </YStack>

                    {/* App Metadata */}
                    <YStack gap={8}>
                        <H6Emphasized>Application Metadata</H6Emphasized>
                        <AppMetadataPanel appMetadata={data.appMetadata} />
                    </YStack>

                    {/* Fork / Retry */}
                    {isForkable && (
                        <YStack
                            gap={8}
                            padding={12}
                            borderRadius={8}
                            style={{ backgroundColor: '#fff3e0' }}
                        >
                            <BodyMediumEmphasized>
                                Retry (Fork) Workflow
                            </BodyMediumEmphasized>
                            <BodySmallRegular color="$gray1000">
                                Create a new workflow execution forked from this
                                one. Earlier steps reuse cached outputs;
                                execution restarts from the selected step.
                                Requires SUPERADMIN role.
                            </BodySmallRegular>
                            <XStack gap={8} alignItems="center">
                                <Dropdown
                                    value={selectedStartStep}
                                    options={[
                                        {
                                            label: 'Step 0: Re-run entire workflow',
                                            value: 0,
                                        },
                                        ...stepOptions,
                                    ]}
                                    onChange={(e) => {
                                        setSelectedStartStep(e.value as number);
                                    }}
                                    placeholder="Select start step"
                                    style={{
                                        fontSize: '0.8rem',
                                        minWidth: 280,
                                    }}
                                />
                                <Button
                                    variant="primary"
                                    size="small"
                                    onPress={handleFork}
                                    disabled={forkMutation.isPending}
                                >
                                    {forkMutation.isPending
                                        ? 'Forking...'
                                        : 'Fork Workflow'}
                                </Button>
                            </XStack>
                        </YStack>
                    )}
                </YStack>
            )}
        </Dialog>
    );
};

// --- Helper sub-components ---

const MetaField = ({
    label,
    value,
    color,
    bold,
    onClick,
}: {
    label: string;
    value: string;
    color?: string;
    bold?: boolean;
    onClick?: () => void;
}) => (
    <YStack minWidth={140}>
        <BodySmallRegular color="$gray1000">{label}</BodySmallRegular>
        <BodyMediumRegular
            style={{
                color: color ?? '#333',
                fontWeight: bold ? 'bold' : 'normal',
                cursor: onClick != null ? 'pointer' : 'default',
                textDecoration: onClick != null ? 'underline' : 'none',
            }}
            onPress={onClick}
        >
            {value}
        </BodyMediumRegular>
    </YStack>
);

const AppMetadataPanel = ({
    appMetadata,
}: {
    appMetadata: DbosAppMetadata;
}) => {
    if (appMetadata.source == null) {
        return (
            <BodySmallRegular color="$gray1000">
                No application metadata linked to this workflow.
            </BodySmallRegular>
        );
    }

    // Determine which metadata object to display
    let metadataObj: object | null = null;
    switch (appMetadata.source) {
        case 'IctDeposit':
            metadataObj = appMetadata.ictDeposit;
            break;
        case 'IctWithdrawal':
            metadataObj = appMetadata.ictWithdrawal;
            break;
        case 'ICTBatchReconciliation':
            metadataObj = appMetadata.batchReconciliation;
            break;
        case 'ApexWebhookMessage':
            metadataObj = appMetadata.webhookMessage;
            break;
    }

    if (metadataObj == null) {
        return (
            <BodySmallRegular color="$gray1000">
                Source: {appMetadata.source} (no metadata available)
            </BodySmallRegular>
        );
    }

    return (
        <YStack gap={4}>
            <BodySmallEmphasized>
                Source: {appMetadata.source}
            </BodySmallEmphasized>
            <pre
                style={{
                    fontSize: '0.7rem',
                    backgroundColor: '#f5f5f5',
                    padding: 8,
                    borderRadius: 4,
                    overflow: 'auto',
                    maxHeight: 300,
                }}
            >
                {JSON.stringify(metadataObj, null, 2)}
            </pre>
        </YStack>
    );
};
